import { useCallback, useEffect, useState } from 'react';
import type { DemWarehouseAddress } from '../../types/dem';
import { composeDeliveryAddress } from '../../lib/demCustomers';
import {
  fetchLalamoveServiceTypes,
  type LalamoveSpecialRequestOption,
} from '../../lib/deliveryArrangements';
import {
  fetchDeliveryWarehouseAddresses,
  saveDeliveryWarehouseAddresses,
} from '../../lib/deliverySettings';
import {
  applyDriverNotesTemplate,
  DEFAULT_DRIVER_NOTES_TEMPLATE,
  fetchDeliveryFormSettings,
  saveDeliveryFormSettings,
} from '../../lib/deliveryFormSettings';
import { LalamoveSpecialRequestsPicker } from './LalamoveSpecialRequestsPicker';

function newWarehouseAddress(): DemWarehouseAddress {
  return {
    id: crypto.randomUUID(),
    label: '',
    name: '',
    phone: '',
    address: '',
    unitNo: '',
    building: '',
    postal: '',
  };
}

/** Delivery form defaults + warehouse addresses (webhook lives under Integrations → Lalamove). */
export function DemDeliverySettingsPanel() {
  const [rows, setRows] = useState<DemWarehouseAddress[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [driverNotesTemplate, setDriverNotesTemplate] = useState(DEFAULT_DRIVER_NOTES_TEMPLATE);
  const [defaultSpecialRequests, setDefaultSpecialRequests] = useState<string[]>([]);
  const [specialRequestOptions, setSpecialRequestOptions] = useState<LalamoveSpecialRequestOption[]>([]);
  const [formSettingsSaving, setFormSettingsSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [warehouses, formSettings, vehicles] = await Promise.all([
        fetchDeliveryWarehouseAddresses(),
        fetchDeliveryFormSettings().catch(() => null),
        fetchLalamoveServiceTypes().catch(() => []),
      ]);
      setRows(warehouses);
      if (formSettings) {
        setDriverNotesTemplate(formSettings.driverNotesTemplate);
        setDefaultSpecialRequests(formSettings.defaultSpecialRequests);
      }
      const uniqueSpecialRequests = new Map<string, LalamoveSpecialRequestOption>();
      for (const vehicle of vehicles) {
        for (const option of vehicle.specialRequests ?? []) {
          if (!uniqueSpecialRequests.has(option.name)) {
            uniqueSpecialRequests.set(option.name, option);
          }
        }
      }
      setSpecialRequestOptions([...uniqueSpecialRequests.values()]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load warehouse addresses.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const updateRow = (id: string, patch: Partial<DemWarehouseAddress>) => {
    setRows((prev) => prev.map((row) => (row.id === id ? { ...row, ...patch } : row)));
  };

  const handleSave = async () => {
    const cleaned = rows
      .map((row) => ({
        ...row,
        label: row.label.trim(),
        name: row.name.trim(),
        phone: row.phone.trim(),
        address: row.address.trim(),
        unitNo: (row.unitNo ?? '').trim() || undefined,
        building: (row.building ?? '').trim() || undefined,
        postal: row.postal.trim(),
      }))
      .filter((row) => row.label && row.address && row.postal);

    if (
      rows.some((row) => row.label.trim() || row.address.trim() || row.postal.trim()) &&
      cleaned.length === 0
    ) {
      setError('Each warehouse needs a label, address, and 6-digit postal code.');
      return;
    }

    if (cleaned.some((row) => !/^\d{6}$/.test(row.postal))) {
      setError('Postal code must be a 6-digit Singapore postcode.');
      return;
    }

    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      await saveDeliveryWarehouseAddresses(cleaned);
      setRows(cleaned);
      setNotice('Warehouse addresses saved.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save warehouse addresses.');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveFormSettings = async () => {
    setFormSettingsSaving(true);
    setError(null);
    setNotice(null);
    try {
      await saveDeliveryFormSettings({
        driverNotesTemplate: applyDriverNotesTemplate(driverNotesTemplate),
        defaultSpecialRequests,
      });
      setNotice('Delivery form defaults saved.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save delivery form defaults.');
    } finally {
      setFormSettingsSaving(false);
    }
  };

  return (
    <section className="card settings-panel">
      <div className="settings-panel__head">
        <div>
          <h2 className="settings-panel__title">Deliveries</h2>
        </div>
        <button type="button" className="btn-secondary btn-sm" onClick={() => void load()} disabled={loading}>
          Refresh
        </button>
      </div>

      {error ? <div className="alert alert-error">{error}</div> : null}
      {notice ? <div className="alert alert-success">{notice}</div> : null}

      <div className="dem-delivery-settings__form-defaults">
        <h3 className="settings-panel__subtitle">Delivery form defaults</h3>
        <label className="field">
          <span className="field-label">Driver notes template</span>
          <textarea
            className="field-input"
            rows={5}
            value={driverNotesTemplate}
            onChange={(e) => setDriverNotesTemplate(e.target.value)}
          />
          <p className="settings-panel__lead">
            Use <code>[quantity]</code> as a manual placeholder — it is not auto-calculated.
          </p>
        </label>
        <div className="field">
          <span className="field-label">Default Lalamove additional services</span>
          <LalamoveSpecialRequestsPicker
            options={specialRequestOptions}
            selected={defaultSpecialRequests}
            onChange={setDefaultSpecialRequests}
            disabled={loading}
          />
        </div>
        <div className="dem-delivery-settings__actions">
          <button
            type="button"
            className="btn-primary btn-sm"
            disabled={formSettingsSaving || loading}
            onClick={() => void handleSaveFormSettings()}
          >
            {formSettingsSaving ? 'Saving…' : 'Save form defaults'}
          </button>
        </div>
      </div>

      {loading ? (
        <p className="muted">Loading…</p>
      ) : (
        <div className="dem-delivery-settings">
          <h3 className="settings-panel__subtitle">Warehouse addresses</h3>
          {rows.length === 0 ? (
            <p className="dem-delivery-settings__empty">No warehouse addresses yet.</p>
          ) : (
            rows.map((row) => (
              <div key={row.id} className="dem-delivery-settings__row">
                <div className="dem-delivery-settings__row-head">
                  <input
                    className="field-input"
                    value={row.label}
                    onChange={(e) => updateRow(row.id, { label: e.target.value })}
                    placeholder="Label (e.g. Main warehouse)"
                    aria-label="Warehouse label"
                  />
                  <button
                    type="button"
                    className="btn-secondary btn-sm"
                    onClick={() => setRows((prev) => prev.filter((item) => item.id !== row.id))}
                  >
                    Remove
                  </button>
                </div>
                <div className="dem-delivery-settings__address-grid">
                  <label className="field">
                    <span className="field-label">Contact name</span>
                    <input
                      className="field-input"
                      value={row.name}
                      onChange={(e) => updateRow(row.id, { name: e.target.value })}
                    />
                  </label>
                  <label className="field">
                    <span className="field-label">Phone</span>
                    <input
                      className="field-input"
                      value={row.phone}
                      onChange={(e) => updateRow(row.id, { phone: e.target.value })}
                    />
                  </label>
                  <label className="field">
                    <span className="field-label">Address</span>
                    <input
                      className="field-input"
                      value={row.address}
                      onChange={(e) => updateRow(row.id, { address: e.target.value })}
                    />
                  </label>
                  <label className="field">
                    <span className="field-label">Unit no.</span>
                    <input
                      className="field-input"
                      value={row.unitNo ?? ''}
                      onChange={(e) => updateRow(row.id, { unitNo: e.target.value })}
                    />
                  </label>
                  <label className="field">
                    <span className="field-label">Building</span>
                    <input
                      className="field-input"
                      value={row.building ?? ''}
                      onChange={(e) => updateRow(row.id, { building: e.target.value })}
                    />
                  </label>
                  <label className="field">
                    <span className="field-label">Postal</span>
                    <input
                      className="field-input"
                      value={row.postal}
                      onChange={(e) => updateRow(row.id, { postal: e.target.value })}
                    />
                  </label>
                </div>
                <p className="dem-delivery-settings__preview">
                  Preview: {composeDeliveryAddress(row) || '—'}
                </p>
              </div>
            ))
          )}

          <div className="dem-delivery-settings__actions">
            <button
              type="button"
              className="btn-secondary btn-sm"
              onClick={() => setRows((prev) => [...prev, newWarehouseAddress()])}
            >
              Add warehouse address
            </button>
            <button type="button" className="btn-primary btn-sm" disabled={saving} onClick={() => void handleSave()}>
              {saving ? 'Saving…' : 'Save addresses'}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
